import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Property } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AddressAutocomplete from "@/components/address-autocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { PropertyGroupSelect } from "@/components/property-group-select";
import { Plus, Loader2, AlertCircle, ExternalLink } from "lucide-react";

// Backend now sources listings from Zillow (via Apify); the response
// shape kept the legacy `zillowUrl` field as a mirror of `listingUrl`
// for one release so older clients keep working.
interface ImportedListingData {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  bedrooms: number;
  bathrooms: number;
  listingPrice: string;
  yearBuilt: number | null;
  sqft: number | null;
  lotSize: string;
  propertyType: string;
  description: string;
  mainImage: string;
  // New canonical field (Task #1101). Prefer this when reading.
  listingUrl?: string;
  // Legacy mirror — kept for one release; may be removed later.
  zillowUrl?: string;
  rentEstimateLow?: string;
  rentEstimateHigh?: string;
  rentEstimateMedian?: string;
  ownerName?: string;
}

interface PropertyCreatePayload {
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  state?: string;
  country?: string;
  postalCode?: string;
  roomCount: number;
  bathroomCount: number;
  propertyGroup: string;
  status: string;
  yearBuilt?: number;
  lotSize?: string;
  propertyType?: string;
  description?: string;
  mainImage?: string;
  managementType?: string;
  zillowImport?: Record<string, string>;
}

interface AddPropertyDialogProps {
  defaultStatus?: "active" | "underwriting" | "acquisition";
  forceStatus?: "underwriting" | "acquisition";
  onCreated?: (property: Property) => void;
  disabled?: boolean;
  triggerLabel?: string;
  triggerTestId?: string;
  "data-tour"?: string;
  /** Open on mount (deep link, e.g. /app/properties?add=1 from the connect-PadSplit screen). */
  defaultOpen?: boolean;
}

function parseImportError(error: unknown): string {
  const fallback = "Failed to fetch property data. Please check the URL and try again.";
  if (!(error instanceof Error)) return fallback;
  let msg = error.message || fallback;
  const jsonMatch = msg.match(/^\d+:\s*(\{.*\})$/s);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[1]) as { message?: string };
      if (parsed.message) msg = parsed.message;
    } catch {
      // ignore parse errors
    }
  }
  return msg;
}

export function AddPropertyDialog({
  defaultStatus = "active",
  forceStatus,
  onCreated,
  disabled = false,
  triggerLabel = "Add Property",
  triggerTestId = "button-add-property",
  "data-tour": dataTour,
  defaultOpen = false,
}: AddPropertyDialogProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const effectiveDefaultStatus: "active" | "underwriting" = (forceStatus ?? defaultStatus) as "active" | "underwriting";

  const [dialogOpen, setDialogOpen] = useState(false);
  // Deep-link open: once, as soon as the trigger is enabled (permissions can
  // load after the first render).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!defaultOpen || disabled || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    setDialogOpen(true);
  }, [defaultOpen, disabled]);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newCoords, setNewCoords] = useState<{ lat: number; lng: number } | undefined>();
  const [newZipCode, setNewZipCode] = useState("");
  const [newState, setNewState] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [newGroup, setNewGroup] = useState("");
  const [newStatus, setNewStatus] = useState<"active" | "underwriting" | "acquisition">(effectiveDefaultStatus);
  const [newRoomCount, setNewRoomCount] = useState("");
  const [newBathroomCount, setNewBathroomCount] = useState("");
  const [newManagementType, setNewManagementType] = useState<"padsplit" | "self_managed">("padsplit");
  // Task #1904 — Optional inline Mercury card provisioning on property
  // create. The toggle is only shown when the host has a working Mercury
  // connection AND beta access (status endpoint returns 200 with `hasApiKey`).
  // Defaults to ON; flipped on automatically once the status probe confirms
  // the host has Mercury connected + a saved API key + beta access. Hosts can
  // untick before submitting.
  const [createMercuryCard, setCreateMercuryCard] = useState(true);

  const [activeTab, setActiveTab] = useState("manual");
  const [zillowUrl, setZillowUrl] = useState("");
  const [zillowData, setZillowData] = useState<ImportedListingData | null>(null);
  const [zillowError, setZillowError] = useState("");

  const [importName, setImportName] = useState("");
  const [importAddress, setImportAddress] = useState("");
  const [importGroup, setImportGroup] = useState("");
  const [importStatus, setImportStatus] = useState<"active" | "underwriting" | "acquisition">(forceStatus ?? "underwriting");
  const [importBedrooms, setImportBedrooms] = useState("");
  const [importBathrooms, setImportBathrooms] = useState("");
  const [importPrice, setImportPrice] = useState("");
  const [importYearBuilt, setImportYearBuilt] = useState("");
  const [importSqft, setImportSqft] = useState("");
  const [importLotSize, setImportLotSize] = useState("");
  const [importPropertyType, setImportPropertyType] = useState("");
  const [importDescription, setImportDescription] = useState("");

  const resetForm = () => {
    setNewName("");
    setNewAddress("");
    setNewCoords(undefined);
    setNewZipCode("");
    setNewState("");
    setNewCountry("");
    setNewGroup("");
    setNewStatus(effectiveDefaultStatus);
    setNewRoomCount("");
    setNewBathroomCount("");
    setNewManagementType("padsplit");
    setActiveTab("manual");
    setZillowUrl("");
    setZillowData(null);
    setZillowError("");
    setImportName("");
    setImportAddress("");
    setImportGroup("");
    setImportStatus(forceStatus ?? "underwriting");
    setImportBedrooms("");
    setImportBathrooms("");
    setImportPrice("");
    setImportYearBuilt("");
    setImportSqft("");
    setImportLotSize("");
    setImportPropertyType("");
    setImportDescription("");
  };

  // Task #1904 — Probe whether the host has Mercury card provisioning unlocked
  // (Mercury connected + API token saved + beta access). The toggle is only
  // rendered when status is 200 + hasApiKey + has Mercury connected.
  const mercuryCardStatus = useQuery<{ mercuryConnected: boolean; hasApiKey: boolean }>({
    queryKey: ["/api/app/mercury/cards/status"],
    retry: false,
  });
  const showMercuryToggle =
    !!mercuryCardStatus.data?.mercuryConnected && !!mercuryCardStatus.data?.hasApiKey;

  const createMutation = useMutation({
    mutationFn: async (data: PropertyCreatePayload) => {
      const res = await apiRequest("POST", "/api/app/properties", data);
      return res.json() as Promise<Property>;
    },
    onSuccess: async (prop: Property) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      // Task #1904 — Fire-and-forget Mercury card provisioning when the host
      // opted in. Failure here is non-fatal (toast warns, but property
      // creation itself already succeeded).
      if (createMercuryCard && showMercuryToggle) {
        try {
          await apiRequest("POST", "/api/app/mercury/cards", { propertyId: prop.id });
          queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
          toast({ title: "Mercury card created", description: `Card linked to ${prop.name}.` });
        } catch (err) {
          toast({
            title: "Property added — Mercury card creation failed",
            description: parseErrorMessage(err, "Try again from Financials → Settings."),
            variant: "destructive",
          });
        }
      }
      setDialogOpen(false);
      resetForm();
      if (onCreated) {
        onCreated(prop);
      } else if (prop.status === "underwriting") {
        toast({ title: "Property added — opening underwriting analysis" });
        navigate(`/app/underwriting/${prop.id}`);
      } else {
        toast({ title: "Property added" });
      }
    },
    onError: () => {
      toast({ title: "Failed to create property", variant: "destructive" });
    },
  });

  const zillowFetchMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (url: string) => {
      const res = await apiRequest("POST", "/api/app/zillow-import", { url });
      return res.json() as Promise<ImportedListingData>;
    },
    onSuccess: (data: ImportedListingData) => {
      setZillowData(data);
      setZillowError("");
      const streetAddr = data.address.split(",")[0]?.trim() ?? "";
      setImportName(streetAddr);
      setImportAddress(data.address);
      setImportBedrooms(String(data.bedrooms || ""));
      setImportBathrooms(String(data.bathrooms || ""));
      setImportPrice(data.listingPrice);
      setImportYearBuilt(data.yearBuilt ? String(data.yearBuilt) : "");
      setImportSqft(data.sqft ? String(data.sqft) : "");
      setImportLotSize(data.lotSize);
      setImportPropertyType(data.propertyType);
      setImportDescription(data.description);
    },
    onError: (error: unknown) => {
      setZillowError(parseImportError(error));
      setZillowData(null);
    },
  });

  const resolvedManualStatus: "active" | "underwriting" | "acquisition" = forceStatus ?? newStatus;
  const resolvedImportStatus: "active" | "underwriting" | "acquisition" = forceStatus ?? importStatus;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    if (resolvedManualStatus === "active" && (!newRoomCount || !newBathroomCount)) return;
    const payload: PropertyCreatePayload = {
      name: newName.trim(),
      address: newAddress.trim(),
      latitude: newCoords?.lat,
      longitude: newCoords?.lng,
      state: newState || undefined,
      country: newCountry || undefined,
      postalCode: newZipCode || undefined,
      roomCount: parseInt(newRoomCount) || 0,
      bathroomCount: parseInt(newBathroomCount) || 0,
      propertyGroup: newGroup.trim(),
      status: resolvedManualStatus,
      managementType: newManagementType,
    };
    if (resolvedManualStatus === "underwriting" && newZipCode) {
      payload.zillowImport = { zipCode: newZipCode };
    }
    createMutation.mutate(payload);
  };

  const handleZillowFetch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = zillowUrl.trim();
    if (!trimmed) return;
    // Validate the host before hitting the backend so users get
    // immediate feedback when they paste a Zillow / generic URL.
    try {
      const parsed = new URL(trimmed);
      if (!parsed.hostname.endsWith("zillow.com")) {
        setZillowError("URL must be a Zillow listing (zillow.com).");
        return;
      }
    } catch {
      setZillowError("Please paste a valid Zillow listing URL.");
      return;
    }
    zillowFetchMutation.mutate(trimmed);
  };

  const handleImportConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!importName.trim() || !importGroup.trim()) return;

    const payload: PropertyCreatePayload = {
      name: importName.trim(),
      address: importAddress.trim(),
      state: zillowData?.state || undefined,
      country: "United States",
      postalCode: zillowData?.zipCode || undefined,
      roomCount: parseInt(importBedrooms) || 0,
      bathroomCount: parseInt(importBathrooms) || 0,
      propertyGroup: importGroup.trim(),
      status: resolvedImportStatus,
      yearBuilt: importYearBuilt ? parseInt(importYearBuilt) : undefined,
      lotSize: importLotSize || undefined,
      propertyType: importPropertyType || undefined,
      description: importDescription || undefined,
      mainImage: zillowData?.mainImage || undefined,
    };

    if (resolvedImportStatus === "underwriting") {
      payload.zillowImport = {
        // Prefer the new `listingUrl` field; fall back to `zillowUrl`
        // for one release while older API responses are in flight.
        listingLink: (zillowData?.listingUrl ?? zillowData?.zillowUrl) ?? "",
        sqft: importSqft,
        currentBedrooms: importBedrooms,
        currentBathrooms: importBathrooms,
        homePurchasePrice: importPrice,
        zipCode: zillowData?.zipCode ?? "",
        rentEstimateLow: zillowData?.rentEstimateLow ?? "",
        rentEstimateHigh: zillowData?.rentEstimateHigh ?? "",
        rentEstimateMedian: zillowData?.rentEstimateMedian ?? "",
        ownerName: zillowData?.ownerName ?? "",
      };
    }

    createMutation.mutate(payload);
  };

  const isFormValid =
    newName.trim() &&
    newAddress.trim() &&
    newGroup.trim() &&
    (resolvedManualStatus === "underwriting" ||
      resolvedManualStatus === "acquisition" ||
      (parseInt(newRoomCount) > 0 && parseInt(newBathroomCount) > 0));

  const isImportFormValid =
    importName.trim() &&
    importGroup.trim() &&
    (resolvedImportStatus === "underwriting" ||
      resolvedImportStatus === "acquisition" ||
      (parseInt(importBedrooms) > 0 && parseInt(importBathrooms) > 0));

  // Gate every dismissal path while a multi-second write is in flight so a stray
  // close cannot silently drop the user's work: createMutation creates the
  // property (+ optional Mercury card provisioning) and zillowFetchMutation runs
  // an Apify scrape. Both are fired from handlers, not inline JSX, so the dialog
  // lint guard can't see them — they must be gated by hand. See
  // docs/dialog-dismissal-convention.md.
  const isInFlight = createMutation.isPending || zillowFetchMutation.isPending;

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        if (!open && isInFlight) return;
        setDialogOpen(open);
        if (!open) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-brand hover:bg-brand-hover"
          data-testid={triggerTestId}
          data-tour={dataTour}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          if (isInFlight) {
            e.preventDefault();
            return;
          }
          const target = e.target as HTMLElement | null;
          if (target?.closest?.('[data-address-autocomplete-portal]')) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (isInFlight) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isInFlight) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Add Property</DialogTitle>
          <DialogDescription>Create a new property manually or import from Zillow.</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual" data-testid="tab-manual-entry">Manual Entry</TabsTrigger>
            <TabsTrigger value="zillow" data-testid="tab-zillow-import">Import from Zillow</TabsTrigger>
          </TabsList>

          <TabsContent value="manual">
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Name *</label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Property A"
                  data-testid="input-property-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <AddressAutocomplete
                  value={newAddress}
                  onChange={(address) => {
                    setNewAddress(address);
                    setNewCoords(undefined);
                    setNewZipCode("");
                    setNewState("");
                    setNewCountry("");
                  }}
                  onSelect={(address, coords, zipCode, _city, state, _streetAddress, country) => {
                    setNewAddress(address);
                    setNewCoords(coords);
                    if (zipCode) setNewZipCode(zipCode);
                    if (state) setNewState(state);
                    if (country) setNewCountry(country);
                  }}
                  placeholder="Full property address"
                  data-testid="input-property-address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Group *</label>
                <PropertyGroupSelect
                  value={newGroup}
                  onChange={setNewGroup}
                  placeholder="Select a group"
                  testId="select-property-group"
                />
                <p className="text-xs text-gray-500 mt-1">Manage groups from the Properties page header.</p>
              </div>
              {!forceStatus ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                  <Select
                    value={newStatus}
                    onValueChange={(v) => setNewStatus(v as "active" | "underwriting" | "acquisition")}
                  >
                    <SelectTrigger data-testid="select-property-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="underwriting">Underwriting</SelectItem>
                      <SelectItem value="acquisition">Acquisition</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                <Select
                  value={newManagementType}
                  onValueChange={(v) => setNewManagementType(v as "padsplit" | "self_managed")}
                >
                  <SelectTrigger data-testid="select-management-type-new">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="padsplit">PadSplit</SelectItem>
                    <SelectItem value="self_managed">Direct Managed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {resolvedManualStatus === "active" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bedroom Count *</label>
                    <Input
                      type="number"
                      min="1"
                      value={newRoomCount}
                      onChange={(e) => setNewRoomCount(e.target.value)}
                      placeholder="0"
                      data-testid="input-property-rooms"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bathroom Count *</label>
                    <Input
                      type="number"
                      min="1"
                      value={newBathroomCount}
                      onChange={(e) => setNewBathroomCount(e.target.value)}
                      placeholder="0"
                      data-testid="input-property-bathrooms"
                    />
                  </div>
                </div>
              )}

              {resolvedManualStatus === "underwriting" && (
                <p className="text-sm text-amber-600 bg-amber-50 rounded p-3" data-testid="text-uw-note">
                  Underwriting properties will go directly to the underwriting analysis after creation.
                </p>
              )}

              {showMercuryToggle && (
                <label className="flex items-start gap-2 text-sm text-gray-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="toggle-mercury-card-create">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={createMercuryCard}
                    onChange={(e) => setCreateMercuryCard(e.target.checked)}
                    data-testid="input-mercury-card-create"
                  />
                  <span>
                    <strong className="font-medium">Create a Mercury card for this property</strong>
                    <span className="block text-xs text-gray-500 mt-0.5">A virtual Mercury card named "PadPulse – {newName || "property"}" will be created and linked.</span>
                  </span>
                </label>
              )}

              <Button
                type="submit"
                className="w-full bg-brand"
                disabled={createMutation.isPending || !isFormValid}
                data-testid="button-submit-property"
              >
                {createMutation.isPending ? "Creating..." : "Add Property"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="zillow">
            {!zillowData ? (
              <form onSubmit={handleZillowFetch} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zillow Listing URL</label>
                  <Input
                    value={zillowUrl}
                    onChange={(e) => {
                      setZillowUrl(e.target.value);
                      setZillowError("");
                    }}
                    placeholder="https://www.zillow.com/homedetails/..."
                    data-testid="input-zillow-url"
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste a Zillow listing URL to auto-fill property details</p>
                </div>

                {zillowError && (
                  <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded p-3" data-testid="text-zillow-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{zillowError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-brand"
                  disabled={zillowFetchMutation.isPending || !zillowUrl.trim()}
                  data-testid="button-fetch-zillow"
                >
                  {zillowFetchMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Fetching property data...
                    </>
                  ) : (
                    "Fetch Property Data"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleImportConfirm} className="space-y-4 mt-4">
                {zillowData.mainImage && (
                  <div className="rounded-lg overflow-hidden border" data-testid="img-zillow-preview">
                    <img
                      src={zillowData.mainImage}
                      alt="Property"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />
                    <a
                      href={zillowData.listingUrl ?? zillowData.zillowUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      data-testid="link-zillow-listing"
                    >
                      View on Zillow
                    </a>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setZillowData(null);
                      setZillowUrl("");
                    }}
                    data-testid="button-clear-import"
                  >
                    Clear & Start Over
                  </Button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Name *</label>
                  <Input
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="Property name"
                    data-testid="input-import-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <AddressAutocomplete
                    value={importAddress}
                    onChange={(address) => setImportAddress(address)}
                    onSelect={(address) => setImportAddress(address)}
                    placeholder="Full property address"
                    data-testid="input-import-address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Group *</label>
                  <PropertyGroupSelect
                    value={importGroup}
                    onChange={setImportGroup}
                    placeholder="Select a group"
                    testId="select-import-group"
                  />
                </div>
                {!forceStatus ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <Select
                      value={importStatus}
                      onValueChange={(v) => setImportStatus(v as "active" | "underwriting" | "acquisition")}
                    >
                      <SelectTrigger data-testid="select-import-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="underwriting">Underwriting</SelectItem>
                        <SelectItem value="acquisition">Acquisition</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                    <Input
                      type="number"
                      min="0"
                      value={importBedrooms}
                      onChange={(e) => setImportBedrooms(e.target.value)}
                      data-testid="input-import-bedrooms"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                    <Input
                      type="number"
                      min="0"
                      value={importBathrooms}
                      onChange={(e) => setImportBathrooms(e.target.value)}
                      data-testid="input-import-bathrooms"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Listing Price</label>
                    <Input
                      value={importPrice}
                      onChange={(e) => setImportPrice(e.target.value)}
                      placeholder="$0"
                      data-testid="input-import-price"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
                    <Input
                      type="number"
                      value={importYearBuilt}
                      onChange={(e) => setImportYearBuilt(e.target.value)}
                      data-testid="input-import-year-built"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sqft</label>
                    <Input
                      type="number"
                      value={importSqft}
                      onChange={(e) => setImportSqft(e.target.value)}
                      data-testid="input-import-sqft"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lot Size</label>
                    <Input
                      value={importLotSize}
                      onChange={(e) => setImportLotSize(e.target.value)}
                      data-testid="input-import-lot-size"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                  <Input
                    value={importPropertyType}
                    onChange={(e) => setImportPropertyType(e.target.value)}
                    data-testid="input-import-property-type"
                  />
                </div>

                {(zillowData.rentEstimateMedian ||
                  zillowData.rentEstimateLow ||
                  zillowData.rentEstimateHigh ||
                  zillowData.ownerName) && (
                  <div className="bg-blue-50 rounded-lg p-3 space-y-1" data-testid="section-rentcast-data">
                    <p className="text-xs font-semibold text-blue-700 mb-2">Rentcast Data</p>
                    {zillowData.rentEstimateMedian && (
                      <p className="text-sm text-blue-800" data-testid="text-rent-median">
                        Rent Estimate:{" "}
                        <span className="font-medium">
                          ${Number(zillowData.rentEstimateMedian).toLocaleString()}/mo
                        </span>
                      </p>
                    )}
                    {(zillowData.rentEstimateLow || zillowData.rentEstimateHigh) && (
                      <p className="text-sm text-blue-800" data-testid="text-rent-range">
                        Range: ${Number(zillowData.rentEstimateLow || 0).toLocaleString()} –{" "}
                        ${Number(zillowData.rentEstimateHigh || 0).toLocaleString()}/mo
                      </p>
                    )}
                    {zillowData.ownerName && (
                      <p className="text-sm text-blue-800" data-testid="text-owner-name">
                        Owner: <span className="font-medium">{zillowData.ownerName}</span>
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Textarea
                    value={importDescription}
                    onChange={(e) => setImportDescription(e.target.value)}
                    rows={3}
                    className="text-sm"
                    data-testid="input-import-description"
                  />
                </div>

                {resolvedImportStatus === "underwriting" && (
                  <p className="text-sm text-amber-600 bg-amber-50 rounded p-3" data-testid="text-import-uw-note">
                    Underwriting properties will go directly to the underwriting analysis after creation. Zillow and Rentcast data will be auto-populated.
                  </p>
                )}

                {showMercuryToggle && (
                  <label className="flex items-start gap-2 text-sm text-gray-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="toggle-mercury-card-create-import">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={createMercuryCard}
                      onChange={(e) => setCreateMercuryCard(e.target.checked)}
                      data-testid="input-mercury-card-create-import"
                    />
                    <span>
                      <strong className="font-medium">Create a Mercury card for this property</strong>
                      <span className="block text-xs text-gray-500 mt-0.5">A virtual Mercury card named "PadPulse – {importName || "property"}" will be created and linked.</span>
                    </span>
                  </label>
                )}

                <Button
                  type="submit"
                  className="w-full bg-brand"
                  disabled={createMutation.isPending || !isImportFormValid}
                  data-testid="button-confirm-import"
                >
                  {createMutation.isPending ? "Creating..." : "Confirm & Add Property"}
                </Button>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
